import {
   createEncryptionKey,
   createRandomString,
   createUpdatesFromNotes,
   ILocalNoteTree,
   IRemoteStoreConnectionStatus,
   IUpdatableNotepad,
   KeyValueNoteRepo,
} from "@fokused-me/core";
import {
   FMSnackbars,
   ICreateNotepadInfo,
   IManageNotepadsDialogNotepad,
   NotepadContent,
   NotepadType,
   usePromise,
   useSingleton,
} from "@fokused-me/web-ui";
import { List, Map } from "immutable";
import yaml from "js-yaml";
import { useState } from "react";
import { fetchWithTimeout } from "../utils/common";
import { extractImportFileContent } from "../utils/extractImportFileContent";
import { INotepadInfo, INotepadState } from "./Model";
import { readNotepadState, writeNotepadState } from "./notepadStorage";
import { readSystemNotepadInfo, writeSystemNotepadInfo } from "./systemStorage";
import { createLocalStore, deleteLocalStore } from "./utils";

const loadExampleNotes = () =>
   fetchWithTimeout("help/notes.yaml").then((response) => {
      return response.text().then((body) => {
         return yaml.load(body);
      }) as Promise<ILocalNoteTree[]>;
   });

/* eslint-disable-next-line react-hooks/rules-of-hooks */
const useExampleNotesSingleton = useSingleton(() => usePromise(loadExampleNotes));
let exampleNotesValue: ILocalNoteTree[] = [];

/* eslint-disable-next-line react-hooks/rules-of-hooks */
const useNotepadInfosSingleton = useSingleton(() => usePromise(readSystemNotepadInfo));
let notepadInfosValue = List<INotepadInfo>();
let setNotepadInfosValue: React.Dispatch<React.SetStateAction<List<INotepadInfo>>>;
const updateNotepadInfos = (newInfos: List<INotepadInfo>) => {
   setNotepadInfosValue(newInfos);
   return writeSystemNotepadInfo(newInfos);
};

/* eslint-disable-next-line react-hooks/rules-of-hooks */
const useNotepadStateSingleton = useSingleton(() => useState(Map<string, INotepadState>()), [
   Map<string, INotepadState>(),
] as any);
let notepadStateValue = Map<string, INotepadState>();
let setNotepadStateValue: React.Dispatch<React.SetStateAction<Map<string, INotepadState>>>;

/* eslint-disable-next-line react-hooks/rules-of-hooks */
const useNotepadsSingleton = useSingleton(() => useState(Map<string, IUpdatableNotepad>()), [
   Map<string, IUpdatableNotepad>(),
] as any);
let notepadsValue = Map<string, IUpdatableNotepad>();
let setNotepadsValue: React.Dispatch<React.SetStateAction<Map<string, IUpdatableNotepad>>>;

/* eslint-disable-next-line react-hooks/rules-of-hooks */
const useOnlineStatusSingleton = useSingleton(() => useState<IRemoteStoreConnectionStatus | undefined>(), [
   undefined,
] as any);

let onlineStatusValue: IRemoteStoreConnectionStatus | undefined;
let setOnlineStatusValue: React.Dispatch<React.SetStateAction<IRemoteStoreConnectionStatus | undefined>>;

const loadNotepadPromise = (
   notepadInfo: INotepadInfo,
   content?: List<ILocalNoteTree>,
   onConvertedToLocalNotepad?: () => void
) => {
   const notepadId = notepadInfo.id;
   const store = createLocalStore(notepadInfo.localRepo, Buffer.from(notepadInfo.encryptKey, "base64"));
   return readNotepadState(store).then((notepadState) =>
      (notepadInfo.type === "cloud"
         ? import("./Firebase").then((module) => {
              // sync notepad name update from cloud to local
              return module
                 .initFirebaseClient(notepadId, notepadInfo.encryptKey)
                 .getNotepadInfo(notepadId)
                 .then((cloudNotepadInfo) => {
                    if (cloudNotepadInfo) {
                       return cloudNotepadInfo.name !== notepadInfo.name
                          ? updateNotepad(notepadId, { name: cloudNotepadInfo.name }).then(() =>
                               Promise.resolve(cloudNotepadInfo)
                            )
                          : Promise.resolve(cloudNotepadInfo);
                    } else {
                       // couldn't read "remote notepad info" - probably Orphan Cloud notepad
                       onConvertedToLocalNotepad && onConvertedToLocalNotepad();
                       return updateNotepad(notepadId, { type: "local" }, true).then(() => undefined);
                    }
                 })
                 .then((cloudNotepadInfo) => {
                    if (cloudNotepadInfo) {
                       const repo = module.createRemoteAwareRepo(notepadId, store);
                       repo.onStatusChanged((status) => {
                          setOnlineStatusValue(status);
                       });
                       return repo;
                    } else {
                       return new KeyValueNoteRepo(store);
                    }
                 });
           })
         : Promise.resolve(new KeyValueNoteRepo(store))
      ).then((repo) =>
         repo.openNotepad().then((notepad) => {
            notepad.setId(notepadId);
            if (content) {
               const updates = createUpdatesFromNotes(List(content), true);
               notepad.apply(updates);
            }

            console.log("Loaded notepad: ", notepadId);

            notepad.onDataChanged((id, dataHash) => {
               const newState = {
                  id,
                  notes: notepad.getNoteCount(),
                  updated: new Date(notepad.getNoteUpdated()),
                  dataHash,
               } as INotepadState;

               setNotepadStateValue(notepadStateValue.set(id, newState));
               writeNotepadState(store, newState);
            });

            setNotepadsValue(notepadsValue.set(notepadId, notepad));
            setNotepadStateValue(notepadStateValue.set(notepadId, notepadState));

            return {
               notepad,
               store,
            };
         })
      )
   );
};

const createNotepad = (createInfo: ICreateNotepadInfo) => {
   const now = new Date();
   const id = createInfo?.linkInfo?.notepadId || createRandomString(64);

   const notepadInfo: INotepadInfo = {
      id,
      localRepo: createRandomString(8),
      version: 1,
      name: createInfo?.linkInfo?.name || createInfo.name,
      type: createInfo.type === NotepadType.CLOUD ? "cloud" : "local",
      created: now, // TODO should use value from cloudInfo?
      encryptKey: createInfo?.linkInfo?.accessKey || createEncryptionKey(createRandomString(32)).toString("base64"),
   };

   const content =
      createInfo.contentSource === NotepadContent.EXAMPLE
         ? exampleNotesValue
         : createInfo.content
         ? extractImportFileContent(createInfo.content)
         : undefined;

   const createRemoteNotepadPromise =
      notepadInfo.type === "cloud" && createInfo.contentSource !== NotepadContent.LINK
         ? import("./Firebase").then((module) => {
              return module.initFirebaseClient(id, notepadInfo.encryptKey).createNotepad({
                 id,
                 name: notepadInfo.name,
                 version: notepadInfo.version,
                 created: notepadInfo.created,
              });
           })
         : Promise.resolve();

   return createRemoteNotepadPromise.then(() =>
      loadNotepadPromise(notepadInfo, content).then(() => {
         return updateNotepadInfos(notepadInfosValue.push(notepadInfo)).then(() => notepadInfo);
      })
   );
};

const updateNotepad = (notepadId: string, update: Partial<INotepadInfo>, localOnly?: boolean) => {
   const idx = notepadInfosValue.findIndex((it) => it.id === notepadId);
   const info = notepadInfosValue.get(idx!);
   const newInfo: INotepadInfo = { ...info, ...(update as any) };
   let cloudNotepadInfoUpdatePromise = Promise.resolve();

   // update cloud notepad name
   if (!localOnly && info?.type === "cloud" && info.name !== newInfo.name) {
      cloudNotepadInfoUpdatePromise = import("./Firebase").then((module) =>
         module.getFirebaseClient(notepadId).updateNotepad({
            id: notepadId,
            name: newInfo.name,
            created: info.created,
            version: info.version,
         })
      );
   }

   const newNotepadInfos = notepadInfosValue.set(idx!, newInfo);
   return cloudNotepadInfoUpdatePromise.then(() => updateNotepadInfos(newNotepadInfos));
};

const getNotepadInfo = (notepadId: string) => {
   const idx = notepadInfosValue.findIndex((it) => it.id === notepadId);
   const info = notepadInfosValue.get(idx!);
   return info;
};

const deleteNotepad = (notepadId: string, localOnly?: boolean) => {
   const idx = notepadInfosValue.findIndex((it) => it.id === notepadId);
   const info = notepadInfosValue.get(idx);
   const newNotepadInfos = notepadInfosValue.delete(idx!);
   const deleteRemoteNotepadPromise =
      !localOnly && info?.type === "cloud"
         ? import("./Firebase").then((module) => module.getFirebaseClient(notepadId).deleteNotepad(notepadId))
         : Promise.resolve();
   return deleteRemoteNotepadPromise.then(() =>
      deleteLocalStore(info!.localRepo).then(() => updateNotepadInfos(newNotepadInfos))
   );
};

const getCloudNotepadInfo = (notepadId: string, notepadEncryptionKey: string) =>
   import("./Firebase").then((module) =>
      module.initFirebaseClient(notepadId, notepadEncryptionKey).getNotepadInfo(notepadId)
   );

const closeNotepad = (notepadId: string) => {
   return updateNotepad(notepadId, { closed: true }, true).then(
      () => (notepadsValue = notepadsValue.delete(notepadId))
   );
};

const openNotepad = (notepadId: string) => {
   return updateNotepad(notepadId, { closed: false }, true);
};

export function useNotepads() {
   // Static
   const { result: exampleNotes } = useExampleNotesSingleton();
   exampleNotesValue = exampleNotes || exampleNotesValue;

   const { setSnackbar } = FMSnackbars.useContainer();

   // Rarely changes - by Manage Notepads: version, name, closed
   const { result: notepadInfos, setResult: setNotepadInfos } = useNotepadInfosSingleton();
   if (notepadInfos && notepadInfosValue !== notepadInfos) {
      notepadInfosValue = notepadInfos;

      // find notepads not loaded and load them
      const notepadsToLoad = notepadInfos.filter((info) => !notepadsValue.has(info.id) && !info.closed);
      if (!notepadsToLoad.isEmpty()) {
         Promise.all(
            notepadsToLoad
               .map((np) =>
                  loadNotepadPromise(np, undefined, () => {
                     // notify that notepad converted to local
                     setSnackbar({
                        message: `It looks like notepad "${np.name}" was removed from the Cloud. Sync will no longer work for this notepad!`,
                        autoHideDuration: null,
                        severity: "warning",
                     });
                  })
               )
               .toArray()
         ).then(() => Promise.resolve());
      }
   }
   setNotepadInfosValue = (setNotepadInfos as any) || setNotepadInfosValue;

   // Often changes - when data changes
   const ret = useNotepadStateSingleton();
   const [notepadStates, setNotepadStates] = ret;
   notepadStateValue = (notepadStates as any) || notepadStateValue;
   setNotepadStateValue = (setNotepadStates as any) || setNotepadStateValue;

   const [onlineStatus, setOnlineStatus] = useOnlineStatusSingleton();
   onlineStatusValue = onlineStatus || onlineStatusValue;
   setOnlineStatusValue = setOnlineStatus || setOnlineStatusValue;

   // doesn't change
   const [notepads, setNotepads] = useNotepadsSingleton();
   notepadsValue = notepads || notepadsValue;
   setNotepadsValue = setNotepads || setNotepadsValue;

   return {
      notepads: notepadsValue,
      notepadInfos: notepadInfosValue,
      notepadStates: notepadStateValue,
      onlineStatus: onlineStatusValue,
      createNotepad,
      updateNotepad,
      deleteNotepad,
      closeNotepad,
      openNotepad,
      getCloudNotepadInfo,
      getNotepadInfo,
   };
}

export function notepadsToDialog(notepads: List<INotepadInfo>, notepadState?: Map<string, INotepadState>) {
   return notepads
      .map((np) => {
         const state = notepadState?.get(np.id);

         return {
            id: np.id,
            name: np.name,
            closed: np.closed,
            type: np.type === "cloud" ? NotepadType.CLOUD : NotepadType.LOCAL,
            notes: state && state.notes ? `${state.notes} notes` : "",
            updated: state?.updated.toISOString(),
         } as IManageNotepadsDialogNotepad;
      })
      .toArray();
}
